import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRiderAvailability, useSetRiderAvailability } from "@/hooks/use-rider-availability";
import { Loader2 } from "lucide-react";

type AvailabilityState = "available" | "unavailable" | "suspended" | "loading";

function readState(args: {
  isLoading: boolean;
  data: { available: boolean; suspended: boolean } | undefined;
}): AvailabilityState {
  if (args.isLoading || !args.data) {
    return "loading";
  }
  if (args.data.suspended) {
    return "suspended";
  }
  if (args.data.available) {
    return "available";
  }
  return "unavailable";
}

const STATE_VARIANT: Record<
  Exclude<AvailabilityState, "loading">,
  "default" | "secondary" | "destructive"
> = {
  available: "default",
  unavailable: "secondary",
  suspended: "destructive",
};

export function RiderAvailabilityToggle() {
  const { t } = useTranslation();
  const query = useRiderAvailability();
  const mutation = useSetRiderAvailability();
  const [suspendedDialogOpen, setSuspendedDialogOpen] = useState(false);

  const state = readState({ data: query.data, isLoading: query.isPending });
  const isSuspended = state === "suspended";

  const onToggle = async (next: boolean) => {
    try {
      await mutation.mutateAsync(next);
      toast.success(
        next
          ? t("rider.availability.markAvailable", "Marcar como disponible")
          : t("rider.availability.markUnavailable", "Marcar como no disponible"),
      );
    } catch (error) {
      const message = (error as Error).message;
      if (message.toLowerCase().includes("suspended")) {
        setSuspendedDialogOpen(true);
      } else {
        toast.error(message);
      }
    }
  };

  if (state === "loading") {
    return (
      <div
        className="flex items-center gap-2 text-sm text-muted-foreground"
        data-testid="rider-availability-loading"
      >
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        <span>{t("rider.availability.toggle", "Disponibilidad")}</span>
      </div>
    );
  }

  if (isSuspended) {
    return (
      <>
        <div className="flex flex-col items-end gap-1" data-testid="rider-availability-suspended">
          <Badge variant={STATE_VARIANT.suspended}>
            {t("rider.availability.suspended", "Suspendido")}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {t(
              "rider.availability.suspendedHelp",
              "Tu cuenta está suspendida. La disponibilidad la maneja el local; no podés cambiar tu estado desde la app.",
            )}
          </span>
        </div>
        <Dialog open={suspendedDialogOpen} onOpenChange={(open) => setSuspendedDialogOpen(open)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {t("rider.availability.suspendedDialogTitle", "Cuenta suspendida")}
              </DialogTitle>
              <DialogDescription>
                {t(
                  "rider.availability.suspendedDialogBody",
                  "Un repartidor suspendido no puede recibir nuevos pedidos. Solo el local puede reactivarte; desde esta app no es posible cambiar tu disponibilidad mientras estés suspendido.",
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button type="button" onClick={() => setSuspendedDialogOpen(false)}>
                {t("rider.availability.suspendedDialogClose", "Entendido")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  const isAvailable = state === "available";
  const label = isAvailable
    ? t("rider.availability.available", "Disponible")
    : t("rider.availability.unavailable", "No disponible");

  return (
    <button
      type="button"
      onClick={() => onToggle(!isAvailable)}
      disabled={mutation.isPending}
      data-testid="rider-availability-toggle"
      data-state={isAvailable ? "available" : "unavailable"}
      className="flex items-center gap-2 rounded-md border px-3 py-1 text-sm transition-colors hover:bg-accent disabled:opacity-50"
    >
      {mutation.isPending ? (
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      ) : (
        <span
          aria-hidden="true"
          className={`inline-block h-2 w-2 rounded-full ${
            isAvailable ? "bg-green-500" : "bg-muted-foreground/40"
          }`}
        />
      )}
      <Badge variant={isAvailable ? STATE_VARIANT.available : STATE_VARIANT.unavailable}>
        {label}
      </Badge>
    </button>
  );
}
